import React, { useEffect, useRef } from 'react';
import css from './SimpleKeyboard.module.scss';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { SimpleKeyboardLayouts, KeyboardLayouts } from './Layout';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import classNames from 'classnames';
import { KeyboardDispatchTypes, useKeyboardDispatch, useKeyboardState } from '../../context/Keyboard/keyboard';
import { Locales } from '../../context/Localization/localization.types';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';

export interface ISimpleKeyboardProps {
    baseClass?: string;
    submitForm?: () => Promise<void | any>;
    fromModal?: boolean;
}

export const SimpleKeyboard: React.FC<React.PropsWithChildren<ISimpleKeyboardProps>> = ({
    submitForm,
    fromModal,
    ...props
}: ISimpleKeyboardProps) => {
    const locale = useLocale();
    const localization = useLocalization();
    const keyboardDispatch = useKeyboardDispatch();
    const keyboard = useRef<any>();
    const {
        getValue,
        setValue,
        onKeyboardInput,
        onInputChange,
        keyboardLayout,
        keyboardHidden,
        shiftAfterPeriod,
        shiftAfterSpace,
        shiftAfterAmpersand,
        capsLock,
    } = useKeyboardState();

    const lastShiftPressEpoch = useRef(0);

    const inputWasOSK = useRef<boolean>(false);
    const wasBackspace = useRef<boolean>(false);
    const wasShiftBeforeSymbols = useRef<boolean>(false);
    const keyboardBoundsRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        function handleClickOutside(event: { target: any; }) {
            if (keyboardBoundsRef.current && !keyboardBoundsRef.current.contains(event.target)) {
                setTimeout(() => {
                    /*
                     * delay is nessasary because we do not want to close the keyboard if input is not blured.
                     * e.g. the user clicks again on the field, the blur event must occur before this dispatch
                     */
                    keyboardDispatch({ type: KeyboardDispatchTypes.ClickOutOfKeyboard });
                }, 1);
            }
        }

        document.body.addEventListener('click', handleClickOutside);

        return () => {
            document.body.removeEventListener('click', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyboardBoundsRef]);

    const commonKeyboardOptions = {
        physicalKeyboardHighlight: true,
        syncInstanceInputs: true,
        mergeDisplay: true,
        debug: false,
        layout: SimpleKeyboardLayouts[locale as Exclude<Locales, Locales.none>],
        display: {
            '{space}': localization.SpaceBar,
            '{tab}': localization.Tab + ' ⇥',
            '{backspace}': '⌫',
            '{bksp}': '⌫',
            '{enter}': localization.Enter + ' ↵',
            '{numpadenter}': '↵',
            '{shiftinactive}': ' ',
            '{shiftactive}': ' ',
            '{email}': '.com',
            '{@}': '@',
            '{numinactive}': '123',
            '{numactive}': '123',
            '{symbols}': '<>?',
            '{abc}': 'ABC',
            '{empty}': ' ',
            '{emailSpacer}': ' ',
            '{emptyHalfKey}': ' ',
            '{emptyWideKey}': ' ',
            '{boarder}': ' ',
            '{boarderEmail}': ' ',
            '{boarderSpacer}': ' ',
        },
        buttonTheme: [
            {
                class:
                    keyboardLayout === KeyboardLayouts.NumberPad || keyboardLayout === KeyboardLayouts.TelePad
                        ? css.bksp
                        : css.backspace,
                buttons: '{backspace} .com',
            },
            {
                class: capsLock ? css.capslock : css.shiftactive,
                buttons: '{shiftactive}',
            },
            {
                class: css.shiftinactive,
                buttons: '{shiftinactive}',
            },
            {
                class: css.space,
                buttons: '{space}',
            },
            {
                class: css.emailKey,
                buttons: '@gmail.com @yahoo.com @hotmail.com',
            },
            {
                class:
                    keyboardLayout === KeyboardLayouts.SymbolsNumpad
                        ? css.symbolsKey
                        : css.numactive,
                buttons: '{numactive}',
            },
            ...(keyboardLayout === KeyboardLayouts.Symbols
                ? [{
                    class: css.symbolsKey,
                    buttons: '{numinactive}',
                }]
                : []),
            {
                class: css.symbolsKey,
                buttons: '{symbols}',
            },
            {
                class: css.abcKey,
                buttons: '{abc}',
            },
            {
                class: classNames(css.empty, css.helper),
                buttons: '{empty}',
            },
            {
                class: classNames(css.emailSpacer, css.helper),
                buttons: '{emailSpacer}',
            },
            {
                class: classNames(css.emptyHalfKey, css.helper),
                buttons: '{emptyHalfKey}',
            },
            {
                class: classNames(css.emptyWideKey, css.helper),
                buttons: '{emptyWideKey}',
            },
            {
                class: classNames(css.boarder, css.helper),
                buttons: '{boarder}',
            },
            {
                class: classNames(css.boarderEmail, css.helper),
                buttons: '{boarderEmail}',
            },
            {
                class: classNames(css.boarderSpacer, css.helper),
                buttons: '{boarderSpacer}',
            },
        ],
        disableCaretPositioning: false,
    };

    const keyboardOptions: Record<KeyboardLayouts, {}> = {
        [KeyboardLayouts.Alphanumeric]: {
            ...commonKeyboardOptions,
            theme: css.keyboard,
        },
        [KeyboardLayouts.AlphanumericShift]: {
            ...commonKeyboardOptions,
            theme: css.keyboard,
        },
        [KeyboardLayouts.Password]: {
            ...commonKeyboardOptions,
            theme: css.keyboard,
        },
        [KeyboardLayouts.PasswordShift]: {
            ...commonKeyboardOptions,
            theme: css.keyboard,
        },
        [KeyboardLayouts.AlphanumericNumpad]: {
            ...commonKeyboardOptions,
            theme: css.keyboardWithNumpad,
        },
        [KeyboardLayouts.AlphanumericNumpadShift]: {
            ...commonKeyboardOptions,
            theme: css.keyboardWithNumpad,
        },
        [KeyboardLayouts.PasswordNumpad]: {
            ...commonKeyboardOptions,
            theme: css.keyboardWithNumpad,
        },
        [KeyboardLayouts.PasswordNumpadShift]: {
            ...commonKeyboardOptions,
            theme: css.keyboardWithNumpad,
        },
        [KeyboardLayouts.Email]: {
            ...commonKeyboardOptions,
            theme: css.emailKeyboardWithNumpad,
        },
        [KeyboardLayouts.EmailShift]: {
            ...commonKeyboardOptions,
            theme: css.emailKeyboardWithNumpad,
        },
        [KeyboardLayouts.Symbols]: {
            ...commonKeyboardOptions,
            theme: css.keyboard,
        },
        [KeyboardLayouts.SymbolsNumpad]: {
            ...commonKeyboardOptions,
            theme: css.keyboardWithNumpad,
        },
        [KeyboardLayouts.NumberPad]: {
            ...commonKeyboardOptions,
            theme: css.numberPad,
        },
        [KeyboardLayouts.Price]: {
            ...commonKeyboardOptions,
            theme: css.numberPad,
        },
        [KeyboardLayouts.TelePad]: {
            ...commonKeyboardOptions,
            theme: css.numberPad,
        },
    };

    const shiftLayouts = {
        [KeyboardLayouts.Alphanumeric]: KeyboardLayouts.AlphanumericShift,
        [KeyboardLayouts.AlphanumericShift]: KeyboardLayouts.Alphanumeric,
        [KeyboardLayouts.AlphanumericNumpad]: KeyboardLayouts.AlphanumericNumpadShift,
        [KeyboardLayouts.AlphanumericNumpadShift]: KeyboardLayouts.AlphanumericNumpad,
        [KeyboardLayouts.Email]: KeyboardLayouts.EmailShift,
        [KeyboardLayouts.EmailShift]: KeyboardLayouts.Email,
        [KeyboardLayouts.NumberPad]: KeyboardLayouts.NumberPad,
        [KeyboardLayouts.TelePad]: KeyboardLayouts.TelePad,
        [KeyboardLayouts.Price]: KeyboardLayouts.Price,
        [KeyboardLayouts.Password]: KeyboardLayouts.PasswordShift,
        [KeyboardLayouts.PasswordShift]: KeyboardLayouts.Password,
        [KeyboardLayouts.PasswordNumpad]: KeyboardLayouts.PasswordNumpadShift,
        [KeyboardLayouts.PasswordNumpadShift]: KeyboardLayouts.PasswordNumpad,
        [KeyboardLayouts.Symbols]: KeyboardLayouts.Symbols,
        [KeyboardLayouts.SymbolsNumpad]: KeyboardLayouts.SymbolsNumpad,
    };
    const symbolsLayouts = {
        [KeyboardLayouts.Password]: KeyboardLayouts.Symbols,
        [KeyboardLayouts.PasswordShift]: KeyboardLayouts.Symbols,
        [KeyboardLayouts.PasswordNumpad]: KeyboardLayouts.SymbolsNumpad,
        [KeyboardLayouts.PasswordNumpadShift]: KeyboardLayouts.SymbolsNumpad,
    };

    // Shift and CapsLock status are retained when switching between symbols layout and alphanumeric.
    const shouldRestoreShift = capsLock || wasShiftBeforeSymbols.current;
    const abcLayouts = {
        [KeyboardLayouts.Symbols]: shouldRestoreShift ? KeyboardLayouts.PasswordShift : KeyboardLayouts.Password,
        [KeyboardLayouts.SymbolsNumpad]: shouldRestoreShift ? KeyboardLayouts.PasswordNumpadShift : KeyboardLayouts.PasswordNumpad,
    };

    const handleShiftPressed = () => {
        keyboardDispatch({
            type: KeyboardDispatchTypes.KeyboardLayoutChanged,
            payload: shiftLayouts[keyboardLayout],
        });
    };

    const handleCapsLockPressed = () => {
        const capslockLayouts = {
            [KeyboardLayouts.Alphanumeric]: capsLock ? KeyboardLayouts.Alphanumeric : KeyboardLayouts.AlphanumericShift,
            [KeyboardLayouts.AlphanumericShift]: capsLock ? KeyboardLayouts.Alphanumeric : KeyboardLayouts.AlphanumericShift,
            [KeyboardLayouts.AlphanumericNumpad]: capsLock ? KeyboardLayouts.AlphanumericNumpad : KeyboardLayouts.AlphanumericNumpadShift,
            [KeyboardLayouts.AlphanumericNumpadShift]: capsLock ? KeyboardLayouts.AlphanumericNumpad : KeyboardLayouts.AlphanumericNumpadShift,
            [KeyboardLayouts.Email]: capsLock ? KeyboardLayouts.Email : KeyboardLayouts.EmailShift,
            [KeyboardLayouts.EmailShift]: capsLock ? KeyboardLayouts.Email : KeyboardLayouts.EmailShift,
            [KeyboardLayouts.NumberPad]: KeyboardLayouts.NumberPad,
            [KeyboardLayouts.TelePad]: KeyboardLayouts.TelePad,
            [KeyboardLayouts.Price]: KeyboardLayouts.Price,
            [KeyboardLayouts.Password]: capsLock ? KeyboardLayouts.Password : KeyboardLayouts.PasswordShift,
            [KeyboardLayouts.PasswordShift]: capsLock ? KeyboardLayouts.Password : KeyboardLayouts.PasswordShift,
            [KeyboardLayouts.PasswordNumpad]: capsLock ? KeyboardLayouts.PasswordNumpad : KeyboardLayouts.PasswordNumpadShift,
            [KeyboardLayouts.PasswordNumpadShift]: capsLock ? KeyboardLayouts.PasswordNumpad : KeyboardLayouts.PasswordNumpadShift,
            [KeyboardLayouts.Symbols]: KeyboardLayouts.Symbols,
            [KeyboardLayouts.SymbolsNumpad]: KeyboardLayouts.SymbolsNumpad,
        };

        keyboardDispatch({
            type: KeyboardDispatchTypes.CapsLockChanged,
            payload: {
                capslock: !capsLock,
                keyboardLayout: capslockLayouts[keyboardLayout],
            },
        });
    };

    const handleNumPressed = () => {
        const numLayouts = {
            [KeyboardLayouts.Alphanumeric]: KeyboardLayouts.AlphanumericNumpad,
            [KeyboardLayouts.AlphanumericShift]: KeyboardLayouts.AlphanumericNumpadShift,
            [KeyboardLayouts.AlphanumericNumpad]: KeyboardLayouts.Alphanumeric,
            [KeyboardLayouts.AlphanumericNumpadShift]: KeyboardLayouts.AlphanumericShift,
            [KeyboardLayouts.Email]: KeyboardLayouts.Email,
            [KeyboardLayouts.EmailShift]: KeyboardLayouts.EmailShift,
            [KeyboardLayouts.NumberPad]: KeyboardLayouts.NumberPad,
            [KeyboardLayouts.TelePad]: KeyboardLayouts.TelePad,
            [KeyboardLayouts.Price]: KeyboardLayouts.Price,
            [KeyboardLayouts.Password]: KeyboardLayouts.PasswordNumpad,
            [KeyboardLayouts.PasswordShift]: KeyboardLayouts.PasswordNumpadShift,
            [KeyboardLayouts.PasswordNumpad]: KeyboardLayouts.Password,
            [KeyboardLayouts.PasswordNumpadShift]: KeyboardLayouts.PasswordShift,
            [KeyboardLayouts.Symbols]: KeyboardLayouts.SymbolsNumpad,
            [KeyboardLayouts.SymbolsNumpad]: KeyboardLayouts.Symbols,
        };

        keyboardDispatch({
            type: KeyboardDispatchTypes.KeyboardLayoutChanged,
            payload: numLayouts[keyboardLayout],
        });
    };

    const wasSelectionRange = useRef<boolean>(false);

    return (
        isElectron() && !keyboardHidden ?
            <div
                ref={keyboardBoundsRef}
                className={classNames(css.keyboardContainer, fromModal && css.fromModal)}
                data-testid={'keyboard'}
            >
                <Keyboard
                    data-testid={'mock-keyboard'}
                    keyboardRef={(r: any) => {
                        keyboard.current = r;
                    }}
                    beforeInputUpdate={(instance) => {
                        onKeyboardInput();
                        keyboardDispatch({ type: KeyboardDispatchTypes.OnKeyboardInput });

                        const { inputValue, caretPositionStart, caretPositionEnd } = getValue();

                        keyboard.current.setInput(inputValue);
                        keyboard.current.setCaretPosition(caretPositionStart, caretPositionEnd);
                    }}
                    baseClass={props.baseClass}
                    onChange={(input: string) => {
                        const { value, caretPosition } = onInputChange({
                            value: input,
                            caretPosition: keyboard.current.getCaretPosition(),
                            wasBackspace: wasBackspace.current,
                            wasSelectionRange: wasSelectionRange.current,
                        });

                        keyboard.current.setInput(value);
                        keyboard.current.setCaretPosition(caretPosition);

                        setValue({
                            value: value,
                            caretPosition: caretPosition,
                        });

                        if (
                            value.length === 0 &&
                            (keyboardLayout === KeyboardLayouts.Alphanumeric ||
                                keyboardLayout === KeyboardLayouts.AlphanumericNumpad)
                        ) {
                            handleShiftPressed();
                        }
                    }}
                    onKeyPress={(button: string) => {
                        inputWasOSK.current = true;

                        const isShiftLayout = (
                            keyboardLayout === KeyboardLayouts.EmailShift ||
                            keyboardLayout === KeyboardLayouts.AlphanumericShift ||
                            keyboardLayout === KeyboardLayouts.AlphanumericNumpadShift
                        );

                        if (button === '{enter}' || button === '{numpadenter}') {
                            submitForm && submitForm();
                        }
                        else if (button === '{numactive}' || button === '{numinactive}') {
                            handleNumPressed();
                        }
                        else if (button === '{symbols}') {
                            wasShiftBeforeSymbols.current = (
                                keyboardLayout === KeyboardLayouts.PasswordShift ||
                                keyboardLayout === KeyboardLayouts.PasswordNumpadShift
                            );
                            keyboardDispatch({
                                type: KeyboardDispatchTypes.KeyboardLayoutChanged,
                                payload: symbolsLayouts[keyboardLayout as keyof typeof symbolsLayouts],
                            });
                        }
                        else if (button === '{abc}') {
                            keyboardDispatch({
                                type: KeyboardDispatchTypes.KeyboardLayoutChanged,
                                payload: abcLayouts[keyboardLayout as keyof typeof abcLayouts],
                            });
                            wasShiftBeforeSymbols.current = false;
                        }
                        else if (button === '{capslock}') {
                            handleCapsLockPressed();
                        }
                        else if (button === '{shiftinactive}' || button === '{shiftactive}') {
                            const timestamp = Date.now();
                            if (capsLock || lastShiftPressEpoch.current + 300 > timestamp) {
                                handleCapsLockPressed();
                            }
                            else {
                                handleShiftPressed();
                            }

                            lastShiftPressEpoch.current = timestamp;
                        }
                        else {

                            if (button === '{backspace}' || button === '{bksp}') {
                                const val = getValue();

                                if (val.caretPositionStart > 1) {
                                    button = val.inputValue[val.caretPositionStart - 2];

                                    if (button === ' ') {
                                        button = '{space}';
                                    }
                                }
                            }

                            if (
                                !isShiftLayout &&
                                (
                                    (shiftAfterPeriod && button === '.') ||
                                    (shiftAfterSpace && button === '{space}') ||
                                    (shiftAfterAmpersand && button === '&')
                                )
                            ) {
                                handleShiftPressed();
                            }
                            else if (
                                button?.length === 1 &&
                                button.match(/[a-zA-Z]/) &&
                                capsLock !== isShiftLayout
                            ) {
                                // Switches noncaps to caps if capslock is off, and vice versa if capslock is on.
                                keyboardDispatch({
                                    type: KeyboardDispatchTypes.KeyboardLayoutChanged,
                                    payload: shiftLayouts[keyboardLayout],
                                });
                            }
                        }

                        wasBackspace.current = button === '{backspace}' || button === '{bksp}';
                    }}
                    layoutName={keyboardLayout}
                    preventMouseDownDefault={true}
                    {...keyboardOptions[keyboardLayout]}
                />
            </div> : null
    );
};
